import { APIModel } from '@shared/interfaces';

import { Address, Company } from '@/organisations/models';
import { TaxRole } from '@/tax-detail/models/tax-role.enum';
import { emptyStringAsUndefined } from '@/tax-detail/utils';
import { FileStatus, QuestionnaireStatus, getLocalizedFileStatus, getLocalizedQuestionnaireStatus } from '@/tax/models';

import { sortSocialSecurityNumbers } from '../utils';
import { PersonType, getLocalizedPersonType } from './person-type.enum';

import { environment } from '@env';

export type HoldersInfo = {
    hasPartner: boolean;
    declarantLabel: string;
    ownerIsDeclarant: boolean;
    partnerLabel?: string;
};

export class PersonalTaxFile implements APIModel {
    id = -1;
    organisation = -1;
    companyLink = '';
    periodId = -1;
    incomeYear = -1;
    fileStatus: FileStatus = FileStatus.Init;
    localizedFileStatus?: string;
    questionnaireStatus: QuestionnaireStatus = QuestionnaireStatus.None;
    localizedQuestionnaireStatus?: string;
    company = new Company();
    deadline?: Date;
    socialSecurityNumberOther?: string;
    nameOther?: string;
    emailOther?: string;
    address?: Address;
    fileStatusOptions?: { value: FileStatus; label: string }[];
    questionnaireStatusOptions?: { value: QuestionnaireStatus; label: string }[];
    hasQuestionnaire = false;
    mandates = [] as string[];

    hasOther(): boolean {
        return [this.emailOther, this.socialSecurityNumberOther, this.nameOther].some((elem) => (elem ?? '').trim().length !== 0);
    }

    getHoldersInfo(): HoldersInfo {
        if (!this.hasOther()) {
            return {
                hasPartner: false,
                ownerIsDeclarant: true,
                declarantLabel: this.company.name,
            };
        }
        const sortedSsns = sortSocialSecurityNumbers(this.company.socialSecurityNumber, this.socialSecurityNumberOther);
        const ownerIsDeclarant = sortedSsns.length === 0 || sortedSsns[0] === this.company.socialSecurityNumber;
        return {
            hasPartner: true,
            ownerIsDeclarant: ownerIsDeclarant,
            declarantLabel: ownerIsDeclarant
                ? this.company.name
                : emptyStringAsUndefined(this.nameOther) ?? getLocalizedPersonType()[PersonType.Partner],
            partnerLabel: ownerIsDeclarant
                ? emptyStringAsUndefined(this.nameOther) ?? getLocalizedPersonType()[PersonType.Partner]
                : this.company.name,
        };
    }

    getHolderLabel(pType: PersonType): string | undefined {
        const holdersInfo = this.getHoldersInfo();
        if (pType === PersonType.Owner) {
            return holdersInfo.declarantLabel;
        } else {
            return holdersInfo.partnerLabel;
        }
    }

    convertPersonToTaxRole(pRole?: PersonType): TaxRole | undefined {
        if (pRole === undefined) {
            return undefined;
        }
        if (this.getHoldersInfo().ownerIsDeclarant) {
            return pRole == PersonType.Owner ? TaxRole.Owner : TaxRole.Other;
        }
        return pRole == PersonType.Owner ? TaxRole.Other : TaxRole.Owner;
    }

    deserialize(data: any): this {
        this.id = data.id ?? -1;
        this.periodId = data.period_id ?? -1;
        this.organisation = data.organisation ?? -1;
        this.incomeYear = data.fiscal_year ?? -1;
        this.fileStatus = data.file_status ?? this.fileStatus;
        this.localizedFileStatus = getLocalizedFileStatus()[this.fileStatus];
        this.questionnaireStatus = data.questionnaire_status ?? this.questionnaireStatus;
        this.company = data.company ? new Company().deserialize(data.company) : new Company();
        this.companyLink = this.company
            ? environment.silverfinCompanyLink(this.company.sfFirmId, this.company.sfCompanyId, this.periodId)
            : '';
        this.localizedQuestionnaireStatus = getLocalizedQuestionnaireStatus()[this.questionnaireStatus];
        this.deadline = data.deadline ? new Date(data.deadline) : undefined;
        this.socialSecurityNumberOther = data.social_security_number_other?.replace(/[^0-9]/g, '') ?? '';
        this.nameOther = data.name_other ?? undefined;
        this.emailOther = data.email_other ?? undefined;
        this.address = data.address ? new Address().deserialize(data.address) : undefined;
        this.hasQuestionnaire = data.has_questionnaire ?? false;
        this.mandates = data.mandates ?? [];

        if (this.fileStatus === FileStatus.Deleted) {
            this.fileStatusOptions = [
                {
                    label: getLocalizedFileStatus()[FileStatus.Deleted],
                    value: this.fileStatus,
                },
            ];
        } else if (data.file_status_options != undefined) {
            this.fileStatusOptions = data.file_status_options
                .filter((fileStatus: FileStatus) => fileStatus !== FileStatus.Deleted)
                .map((fileStatus: FileStatus) => {
                    return {
                        label: getLocalizedFileStatus()[fileStatus],
                        value: fileStatus,
                    };
                });
        } else {
            this.fileStatusOptions = Object.values(FileStatus)
                .filter((fileStatus: FileStatus) => fileStatus !== FileStatus.Deleted)
                .map((fileStatus: FileStatus) => {
                    return {
                        label: getLocalizedFileStatus()[fileStatus],
                        value: fileStatus,
                    };
                });
        }
        if (data.questionnaire_status_options != undefined) {
            this.questionnaireStatusOptions = data.questionnaire_status_options.map((qStatus: QuestionnaireStatus) => {
                return {
                    label: getLocalizedQuestionnaireStatus()[qStatus],
                    value: qStatus,
                };
            });
        } else {
            this.questionnaireStatusOptions = Object.values(QuestionnaireStatus).map((qStatus: QuestionnaireStatus) => {
                return {
                    label: getLocalizedQuestionnaireStatus()[qStatus],
                    value: qStatus,
                };
            });
        }
        return this;
    }

    serialize(): any {
        return {
            id: this.id,
            'period_id': this.periodId,
            deadline: this.deadline,
            organisation: this.organisation,
            'fiscal_year': this.incomeYear,
            'file_status': this.fileStatus,
            'questionnaire_status': this.questionnaireStatus,
            company: this.company.serialize(),
            'social_security_number_other': this.socialSecurityNumberOther?.replace(/[^0-9]/g, ''),
            'name_other': this.nameOther,
            'email_other': this.emailOther,
            address: this.address?.serialize() ?? null, //Null to remove the address
            'file_status_options': this.fileStatusOptions?.map((o) => o.value),
            'questionnaire_status_options': this.questionnaireStatusOptions?.map((o) => o.value),
            'has_questionnaire': this.hasQuestionnaire,
            mandates: this.mandates,
        };
    }

    copy(): PersonalTaxFile {
        let company = this.company.copy();
        let address = this.address?.clone();
        let taxFileCopy = new PersonalTaxFile().deserialize(this.serialize());
        taxFileCopy.company = company;
        taxFileCopy.address = address;
        return taxFileCopy;
    }

    static Sort(f1: PersonalTaxFile, f2: PersonalTaxFile): number {
        if (f1.company.name === '' || f2.company.name === '') {
            return -1;
        } else {
            return f1.company.name !== f2.company.name
                ? f1.company.name.localeCompare(f2.company.name)
                : (f2.incomeYear ?? 0) - (f1.incomeYear ?? 0);
        }
    }
}
